/**
 * AuthGate, wraps the app and shows the appropriate UI based on auth status.
 *
 *  - 'loading'    -> small spinner (initial session check, ~200ms)
 *  - 'signed-out' -> login form (email + send 8-digit code)
 *  - 'sending'    -> "sending..." button state
 *  - 'sent'       -> "check your email" confirmation
 *  - 'signed-in'  -> renders children (the actual app)
 *
 * On sign-in, also kicks off a one-time cloud-state load and applies it to
 * the main store. If the cloud row is empty (first sign-in for this user),
 * the local state is uploaded so the cloud picks up where the user left off.
 *
 * Patch AG: stripped the "click the link in the email if you're on a regular
 * browser" copy from the post-email alert. Supabase is configured for OTP
 * (numeric code) only, with no magic-link fallback, so that sentence was
 * misleading.
 *
 * Patch AH: closed the cross-user data leak. Three guards work together:
 *
 *   1. Pre-cloud-load reset: every sign-in calls resetToDefaults() before
 *      doing anything with the cloud, so the previous user's leftover
 *      localStorage data is gone before snapshotForCloud() can capture it.
 *      Previously the cloud-empty branch uploaded that leftover state to
 *      the new account's row, contaminating it permanently.
 *
 *   2. Render gate: while signed-in but cloud-load isn't yet 'loaded',
 *      we render the loading screen instead of children. Without this,
 *      there's a render gap between status flipping to 'signed-in' and
 *      the useEffect's reset running where the previous user's data
 *      would be visible.
 *
 *   3. Post-sign-out reset: when status transitions out of 'signed-in',
 *      we also reset, so the idle "logged out" period isn't carrying
 *      anyone's data in memory or localStorage. Initial 'loading' to
 *      'signed-out' on a fresh load (no prior session) is NOT a reset;
 *      a wasSignedInRef tracks whether we ever saw 'signed-in' first.
 *
 * resetToDefaults does NOT touch cloud rows. Existing accounts still load
 * their data normally on the next sign-in via loadStateFromCloud.
 *
 * Patch AP: wires the cloudLoaded gate (added in sync.ts). The flag is
 * false until the initial cloud-load completes (apply-branch OR empty-
 * cloud upload), then true. While false, scheduleCloudSave in sync.ts
 * no-ops, which prevents transient default state from being pushed to
 * cloud during the load gap. Flipped back to false on user switch or
 * sign-out so the next session re-gates correctly. (Children still
 * don't render until cloudLoadStatus === 'loaded' so the practical
 * window is narrow, but the gate also covers any code path that runs
 * outside the render tree, like dynamic imports or realtime callbacks
 * landing during load.)
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useAuthStore } from '../state/auth.js';
import { useAppStore } from '../state/store.js';
import { loadStateFromCloud, saveStateToCloud, saveStateToCloudImmediate, setCloudLoaded, installBeforeUnloadFlush } from '../io/supabase/sync.js';
import { subscribeToUserState, unsubscribeFromUserState, onRemoteStateChanged } from '../io/supabase/realtime.js';
import { cloudEnabled } from '../io/supabase/client.js';
import { getCurrentSubdomain } from '../io/tenant.js';
import { LandingScreen } from './LandingScreen.js';

// Persists for this page load; resets on refresh or app relaunch.
// Tracks whether the welcome animation has already been shown this session
// so a post-OTP sign-in goes straight to the app instead of replaying it.
let _sessionIntroPlayed = false;

export function AuthGate({ children }: { children: ReactNode }): JSX.Element {
  const { status, error, pendingEmail, init, signInWithEmail, verifyOtp, reset } = useAuthStore();
  const userId = useAuthStore(s => s.user?.id ?? null);

  // Initialize auth on mount (idempotent).
  useEffect(() => { void init(); }, [init]);

  // On sign-in, fetch cloud state and overlay it. If cloud is empty, upload current local state.
  const [cloudLoadStatus, setCloudLoadStatus] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  const [cloudLoadError, setCloudLoadError] = useState<string | null>(null);

  // introAnimDone: welcome animation has fired (or was already seen this session).
  // introPlayed: both animation and cloud are done  -  safe to render children.
  // Initialise introAnimDone from _sessionIntroPlayed so a post-OTP sign-in
  // (where the auth form's animation already ran) skips straight to cloud-wait.
  const [introAnimDone, setIntroAnimDone] = useState(_sessionIntroPlayed);
  const [introPlayed,   setIntroPlayed  ] = useState(false);

  // Flip introPlayed once BOTH the animation is done AND cloud finished (success or error).
  useEffect(() => {
    if (introAnimDone && (cloudLoadStatus === 'loaded' || cloudLoadStatus === 'error')) {
      _sessionIntroPlayed = true;
      setIntroPlayed(true);
    }
  }, [introAnimDone, cloudLoadStatus]);

  // Safety valve: if signed-in but introAnimDone is still false after 5s,
  // force it true. Handles iOS Safari power-efficiency mode throttling
  // setTimeout callbacks in the LandingScreen phase machine even when the
  // tab is in the foreground, leaving the silk screen permanently visible.
  useEffect(() => {
    if (status !== 'signed-in' || introAnimDone) return;
    const t = setTimeout(() => {
      _sessionIntroPlayed = true;
      setIntroAnimDone(true);
    }, 5000);
    return () => clearTimeout(t);
  }, [status, introAnimDone]);

  useEffect(() => {
    if (status !== 'signed-in' || !userId) return;
    let cancelled = false;
    // Subdomain slug identifies which prospect site's data to read/write.
    // Stable for the lifetime of the page (determined by window.location.hostname).
    const tenant = getCurrentSubdomain() ?? 'rajat';
    // Patch AP: close the cloud-write gate at the START of every load
    // cycle. Any in-flight writes from a prior session/user are now
    // effectively orphaned (their scheduleCloudSave timers can fire but
    // will hit the gate). The gate stays closed until this load reaches
    // a terminal success state below.
    setCloudLoaded(false);
    (async () => {
      setCloudLoadStatus('loading');
      setCloudLoadError(null);
      try {
        // Patch EX: reload-to-recover path. sync.ts sets btc:reload-to-recover
        // after 2+ consecutive save timeouts caused by a stale iOS TCP socket.
        // A page reload clears the browser connection pool (the only reliable
        // fix). Here we detect the flag and preserve the user's data:
        //   - Skip resetToDefaults() so the latest local state (the unsaved edit)
        //     is not erased before we get a chance to upload it.
        //   - Skip loading cloud state so the older cloud row does not overwrite
        //     the local edit.
        //   - Upload local state immediately on the now-fresh connection.
        // zustand's persist middleware writes every state change to localStorage
        // synchronously, so the store already holds the user's unsaved edit when
        // this effect runs after reload.
        let reloadRecover = false;
        try {
          reloadRecover = localStorage.getItem('btc:reload-to-recover') === '1';
          if (reloadRecover) localStorage.removeItem('btc:reload-to-recover');
        } catch { /* localStorage unavailable */ }

        if (reloadRecover) {
          const snapshot = useAppStore.getState().snapshotForCloud();
          let uploadOk = false;
          try {
            await saveStateToCloudImmediate(userId, tenant, snapshot);
            uploadOk = true;
          } catch { /* saveStateToCloudImmediate already notified error */ }
          if (!cancelled) {
            if (uploadOk) {
              setCloudLoadStatus('loaded');
              setCloudLoaded(true);
              installBeforeUnloadFlush();
            } else {
              setCloudLoadStatus('error');
              setCloudLoadError('Could not sync after reconnecting. Reload to retry.');
            }
          }
          return;
        }

        // Patch AH guard #1: reset local state to engine defaults BEFORE
        // touching cloud. This is the critical step for the cloud-empty
        // branch below: without it, snapshotForCloud() returns whatever
        // was sitting in localStorage from the previous user's session
        // and uploads it to the new account's row. With it, that branch
        // uploads clean defaults for a true fresh start.
        useAppStore.getState().resetToDefaults();

        const cloudState = await loadStateFromCloud(userId, tenant);
        if (cancelled) return;
        if (cloudState) {
          // Existing row: overlay cloud data onto the (now-default) local store.
          useAppStore.getState().applyPersistedState(cloudState);
        } else {
          // First sign-in for this account. Post-AH this snapshot is
          // engine defaults, not the previous user's leftover data.
          const snapshot = useAppStore.getState().snapshotForCloud();
          await saveStateToCloud(userId, tenant, snapshot);
        }
        if (!cancelled) {
          setCloudLoadStatus('loaded');
          // Patch AP: open the gate. From here on, scheduleCloudSave
          // writes flow normally. Done AFTER the apply/save above so
          // the apply branch's mutation doesn't itself enqueue a save
          // that would race with what we just did.
          setCloudLoaded(true);
          // Patch BQ: register beforeunload handler that flushes a
          // pending debounced save when the tab closes. Idempotent;
          // installs once, re-sign-ins are no-ops.
          installBeforeUnloadFlush();
        }
      } catch (e) {
        if (cancelled) return;
        setCloudLoadStatus('error');
        setCloudLoadError(e instanceof Error ? e.message : String(e));
        // Gate stays closed on error. Retrying via the error UI's
        // window.location.reload() restarts this effect cleanly.
      }
    })();
    return () => { cancelled = true; };
  }, [status, userId]);

  // Open the realtime subscription once cloud-load succeeds, so any change
  // pushed from another device propagates here. Tear down on sign-out.
  useEffect(() => {
    if (status !== 'signed-in' || !userId || cloudLoadStatus !== 'loaded') return;
    subscribeToUserState(userId);
    const off = onRemoteStateChanged((state) => {
      // A different device wrote a new state, apply it to our store.
      useAppStore.getState().applyPersistedState(state);
    });
    return () => {
      off();
      unsubscribeFromUserState();
    };
  }, [status, userId, cloudLoadStatus]);

  // Patch AH guard #3: clear local state when the user transitions OUT of
  // 'signed-in' (explicit sign-out OR session expiration). Without this,
  // the localStorage entry would survive sign-out, ready to leak into
  // whoever signs in next. Initial 'loading' to 'signed-out' on a fresh
  // page load is NOT treated as a transition out (the user was never in);
  // wasSignedInRef gates that case.
  const wasSignedInRef = useRef(false);
  useEffect(() => {
    if (status === 'signed-in') {
      wasSignedInRef.current = true;
    } else if (wasSignedInRef.current && status === 'signed-out') {
      wasSignedInRef.current = false;
      useAppStore.getState().resetToDefaults();
      setCloudLoadStatus('idle');
      setCloudLoadError(null);
      // Keep introAnimDone in sync with _sessionIntroPlayed so a same-session
      // re-sign-in still skips the animation.
      setIntroAnimDone(_sessionIntroPlayed);
      setIntroPlayed(false);
      // Patch AP: close the gate too. Any leftover scheduled save from
      // before sign-out (still ticking in the debounce) hits the gate
      // and no-ops, so we don't accidentally upload defaults to the
      // just-signed-out account's row.
      setCloudLoaded(false);
    }
  }, [status]);

  // Skip auth entirely when cloud isn't configured (offline-only mode).
  if (!cloudEnabled()) {
    return <>{children}</>;
  }

  if (status === 'loading') {
    // Plain dark cover while session check resolves (~200ms).
    // No text \u2014 cleaner transition into LandingScreen's opening animation.
    return <div style={{ position: 'fixed', inset: 0, background: '#000', zIndex: 100 }} />;
  }

  if (status === 'signed-in') {
    if (!introPlayed) {
      if (!_sessionIntroPlayed && !introAnimDone) {
        // Welcome not yet seen this session \u2014 play it while cloud loads.
        // Once onRevealComplete fires (2.6s), introAnimDone becomes true and
        // we fall through to the plain dark div below so the silk background
        // doesn't persist indefinitely while cloud finishes loading.
        return (
          <LandingScreen
            key="intro"
            autoReveal
            onRevealComplete={() => {
              _sessionIntroPlayed = true;
              setIntroAnimDone(true);
            }}
          />
        );
      }
      // Animation done (or already seen this session) \u2014 hold on a dark screen
      // until cloud finishes, then go straight to the app.
      return <div style={{ position: 'fixed', inset: 0, background: 'var(--bg)' }} />;
    }
    if (cloudLoadStatus === 'error') {
      return (
        <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 'var(--gap-m)' }}>
          <div className="alert">
            <strong>Couldn't load your data.</strong>
            <div className="faint" style={{ fontSize: 'var(--fs-micro)', marginTop: 4 }}>{cloudLoadError}</div>
          </div>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      );
    }
    return <>{children}</>;
  }

  // Signed out (or pending OTP) \u2014 show the cinematic landing screen.
  return (
    <LandingScreen
      key="auth"
      onWelcomeComplete={() => {
        _sessionIntroPlayed = true;
        setIntroAnimDone(true);
      }}
      status={status as 'signed-out' | 'sending' | 'sent' | 'verifying'}
      error={error}
      pendingEmail={pendingEmail}
      onSubmitEmail={(em) => { void signInWithEmail(em); }}
      onSubmitCode={(code) => { void verifyOtp(code); }}
      onReset={reset}
    />
  );
}
